import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaPhone, FaCamera, FaSave } from 'react-icons/fa';
import { getUserProfile, updateUserProfile, clearState } from '../../../redux/Slices/authSlice';
import { useTitle } from '../../../context/TitleContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';

const Profile = () => {
  const { t } = useTranslation('global');
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const { profile, loading, error, success } = useSelector((state) => state.auth);

  // Profile form states
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    image: null
  });

  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    setTitle('الملف الشخصي');
    dispatch(clearState());
    dispatch(getUserProfile());
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (profile) {
      setProfileData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        image: null
      });
      setImagePreview(profile.image);
    }
  }, [profile]);

  useEffect(() => {
    if (success) {
      toast.success(success, {
        position: "top-right",
        rtl: true,
        autoClose: 2000
      });
      dispatch(clearState());
    }
    if (error) {
      toast.error(error, {
        position: "top-right",
        rtl: true
      });
      dispatch(clearState());
    }
  }, [success, error, dispatch]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files[0]) {
      setProfileData(prev => ({ ...prev, [name]: files[0] }));
      setImagePreview(URL.createObjectURL(files[0]));
    } else {
      setProfileData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    
    // إرسال الاسم فقط
    if (profileData.name) {
      formData.append('name', profileData.name);
    }
    
    // إرسال الصورة فقط إذا تم تغييرها
    if (profileData.image) {
      formData.append('image', profileData.image);
    }
    
    dispatch(updateUserProfile(formData));
  };

  const LoadingSpinner = () => (
    <div className="spinner-border text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      <ToastContainer />
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card shadow-lg border-0"
            style={{ borderRadius: '15px' }}
          >
            <div className="card-header bg-white border-0 py-4">
              <h4 className="text-center mb-0 fw-bold" style={{ color: '#184194', fontSize: '1.3rem' }}>
                الملف الشخصي
              </h4>
            </div>

            <div className="card-body p-5">
              <form onSubmit={handleProfileSubmit}>
                {/* Profile Image */}
                <div className="text-center mb-4">
                  <div className="position-relative d-inline-block">
                    <div 
                      className="rounded-circle border border-3 border-primary"
                      style={{ 
                        width: '120px', 
                        height: '120px', 
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa'
                      }}
                    >
                      {imagePreview ? (
                        <img 
                          src={imagePreview} 
                          alt="Profile" 
                          className="w-100 h-100 object-cover"
                        />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                          <FaUser size={48} className="text-muted" />
                        </div>
                      )}
                    </div>
                    <label 
                      className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle p-2"
                      style={{ cursor: 'pointer' }}
                      title="تغيير الصورة"
                    >
                      <FaCamera size={12} />
                      <input
                        type="file"
                        name="image"
                        onChange={handleInputChange}
                        accept="image/*"
                        className="d-none"
                      />
                    </label>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-medium">الاسم</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <FaUser className="text-secondary" />
                      </span>
                      <input
                        type="text"
                        name="name"
                        value={profileData.name}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="أدخل اسمك"
                        required
                        style={{ borderRadius: '0 8px 8px 0' }}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">البريد الإلكتروني</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <FaEnvelope className="text-secondary" />
                      </span>
                      <input
                        type="email"
                        name="email"
                        value={profileData.email}
                        className="form-control"
                        placeholder="البريد الإلكتروني"
                        disabled
                        style={{ borderRadius: '0 8px 8px 0', backgroundColor: '#f8f9fa' }}
                      />
                    </div>
                    <small className="text-muted">لا يمكن تعديل البريد الإلكتروني</small>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-medium">رقم الهاتف</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <FaPhone className="text-secondary" />
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        value={profileData.phone || 'غير محدد'}
                        className="form-control"
                        placeholder="رقم الهاتف"
                        disabled
                        style={{ borderRadius: '0 8px 8px 0', backgroundColor: '#f8f9fa' }}
                      />
                    </div>
                    <small className="text-muted">لا يمكن تعديل رقم الهاتف</small>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <motion.button
                    whileHover={{ 
                      scale: 1.02,
                      backgroundColor: '#184194',
                      color: 'white'
                    }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="btn btn-outline-primary px-4 py-2 fw-medium"
                    style={{ 
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      borderColor: '#184194',
                      color: '#184194',
                      backgroundColor: 'transparent',
                      transition: 'all 0.3s ease'
                    }}
                    disabled={loading}
                  >
                    {loading ? <LoadingSpinner /> : (
                      <>
                        <FaSave className="mx-2" />
                        حفظ التغييرات
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Profile;